// Weather and Water: a water-cycle sim with a Sun and a Wind slider. Lesson, then do it.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { makeLoop } from '@kit/loop';
import type { GameModule, GameContext } from '@shell/types';
import { createSim, step, groundAt, startFront, type WeatherSim } from './sim';

interface Step {
  lesson: string; task: string;
  kind: 'check' | 'choice';
  check?: (s: WeatherSim, sun: number) => boolean;
  hint?: (s: WeatherSim, sun: number) => string;
  choices?: string[]; answer?: number;
  why: string;
  needFront?: boolean; needEarth?: boolean;
}

function tierFor(level: number): 1 | 2 | 3 { return level <= 3 ? 1 : level <= 6 ? 2 : 3; }

const STEPS: Record<1 | 2 | 3, Step[]> = {
  1: [
    { lesson: 'The sun warms the sea. Warm water turns into vapor, a gas you cannot see, and it rises. High up it is cold, so the vapor turns back into tiny drops. Lots of tiny drops make a cloud.', task: 'Turn up the sun and make a cloud, then tap Check.', kind: 'check', check: s => s.clouds.some(c => c.drops >= 5), hint: (s, sun) => sun < 0.4 ? 'More sun. Vapor only rises when the sea is warm.' : 'Give it a moment. The vapor has to climb to the cold air first.', why: 'Sun lifts water as vapor; cold air up high turns it back into drops. That is a cloud.' },
    { lesson: 'A cloud gets heavier as more drops join. When it is heavy enough, the drops fall. That is rain.', task: 'Keep the sun up until a cloud rains, then tap Check.', kind: 'check', check: s => s.rainEvents > 0, hint: () => 'Not yet. Watch the cloud fill up and get darker.', why: 'Rain is just cloud drops that got too heavy to float.' },
    { lesson: 'Rain lands on the sea or on the land. On land it runs downhill in rivers, back to the sea. Then the sun lifts it again. Round and round: the water cycle.', task: 'Where does rain that falls on land end up?', kind: 'choice', choices: ['It flows down rivers back to the sea', 'It disappears forever', 'It goes up to space', 'It turns into rock'], answer: 0, why: 'Watch the river: land rain runs downhill to the sea, and the cycle starts again.' },
  ],
  2: [
    { lesson: 'Wind pushes clouds. When a cloud is pushed up a mountain the air gets colder, so it rains on the mountain side facing the wind.', task: 'Set the wind so clouds drift onto the mountain, and make it rain there. Then tap Check.', kind: 'check', check: s => s.rainOnMountain > 0, hint: (s) => s.clouds.length === 0 ? 'First make clouds with the sun, then push them with the wind.' : 'Blow the wind toward the mountain (slide right) and wait for the cloud to get there.', why: 'Wind carries the cloud over the mountain, where it rains. That is why one side of a mountain is often greener.' },
    { lesson: 'Evaporation needs heat. The sun is the heat.', task: 'Predict: if the sun goes low, what happens to evaporation?', kind: 'choice', choices: ['It slows down', 'It speeds up', 'It stays the same', 'It turns to snow'], answer: 0, why: 'Less sun, less heat, less water lifted. Now prove it.' },
    { lesson: 'A prediction is only good once you test it.', task: 'Slide the sun low and watch the evaporation number. When it reads under 3 per second, tap Check.', kind: 'check', check: (s, sun) => sun <= 0.35 && s.evapRate < 3, hint: (_, sun) => sun > 0.35 ? 'Lower. Slide the sun to the left.' : 'Watch the evaporation readout settle.', why: 'You predicted it and tested it. That is how science works.' },
  ],
  3: [
    { lesson: 'A cold front is a wall of cold air pushing under warm air. The warm air is shoved upward, cools fast, and dumps its water.', task: 'Press Cold front and watch. Where does the rain fall?', kind: 'choice', choices: ['Along the line where cold air meets warm air', 'Only over the sea', 'Only on the mountain top', 'Nowhere, fronts stop rain'], answer: 0, why: 'The front line lifts warm air all at once, so rain falls right along it. Weather maps draw it as a line of blue triangles.', needFront: true },
    { lesson: 'Earth is tilted. In summer your half leans toward the sun, so sunlight hits more directly and days are longer.', task: 'Why is summer warmer than winter?', kind: 'choice', choices: ['Earth is tilted, so the sun hits more directly', 'Earth is closer to the sun in summer', 'The sun gets bigger', 'Clouds go away'], answer: 0, why: 'It is the tilt, not the distance. Earth is actually a little closer to the sun in January.', needEarth: true },
    { lesson: 'The sea is salty but rain is fresh.', task: 'Why is rain not salty?', kind: 'choice', choices: ['Only the water evaporates; the salt stays behind', 'Clouds filter out the salt', 'Rain comes from rivers, not the sea', 'Salt melts in the air'], answer: 0, why: 'Evaporation lifts water molecules only. Salt is left in the sea, which is why the sea stays salty.' },
  ],
};

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.currentLevel();
  const tier = tierFor(level);
  const steps = STEPS[tier];
  let si = 0;
  let sun = 0.5, wind = 0.2;
  let attempts = 0, correct = 0, missed = false;
  let done = false;
  const t0 = performance.now();
  let tStep = performance.now();
  let sim = createSim(400, 300);

  const lesson = h('div.card', { style: { borderLeft: '8px solid var(--science)' } }, h('p', null, ''));
  const task = h('p.center', { style: { fontWeight: '800', minHeight: '44px' } });
  const stage = h('div');
  const readout = h('div.row', { style: { justifyContent: 'space-between', fontWeight: '800', fontSize: '14px' } }, h('span', null), h('span', null), h('span', null), h('span', null));
  const sunS = h('input', { type: 'range', min: '0', max: '100', value: '50', 'aria-label': 'Sun' }) as HTMLInputElement;
  const windS = h('input', { type: 'range', min: '-100', max: '100', value: '20', 'aria-label': 'Wind' }) as HTMLInputElement;
  const sliders = h('div.stack', { style: { gap: '4px' } },
    h('div.row', null, h('span.muted.small', { style: { minWidth: '44px' } }, 'Sun'), sunS),
    h('div.row', null, h('span.muted.small', { style: { minWidth: '44px' } }, 'Wind'), windS),
  );
  const frontBtn = h('button.btn.small', { onclick: () => { startFront(sim); ctx.say('Cold air is pushing in from the left.'); } }, 'Cold front');
  const checkBtn = h('button.btn.primary', { onclick: act }, 'Check');
  const choices = h('div.stack', { style: { gap: '8px' } });
  const actions = h('div.row', null, frontBtn, checkBtn);
  host.append(lesson, task, stage, readout, sliders, actions, choices);
  const S = makeSurface(stage, { aspect: 0.72, draw: false, onResize: () => { sim.W = 400; sim.H = 300; } });
  sunS.addEventListener('input', () => { sun = Number(sunS.value) / 100; });
  windS.addEventListener('input', () => { wind = Number(windS.value) / 100; });
  let showEarth = false;

  function showStep() {
    const st = steps[si];
    (lesson.firstElementChild as HTMLElement).textContent = st.lesson;
    task.textContent = `Step ${si + 1} of 3: ${st.task}`;
    clear(choices);
    missed = false; tStep = performance.now();
    frontBtn.style.display = st.needFront ? '' : 'none';
    showEarth = !!st.needEarth;
    checkBtn.style.display = st.kind === 'check' ? '' : 'none';
    if (st.kind === 'choice') st.choices!.forEach((c, i) => choices.append(h('button.choice', { style: { fontSize: '16px', minHeight: '52px', padding: '8px 12px' }, onclick: () => answer(i) }, c)));
    ctx.speak(st.task);
  }

  function pass(st: Step) {
    if (!missed) { attempts++; correct++; }
    ctx.report({ skill: 'earth_science', level, correct: !missed, hintUsed: missed, ms: performance.now() - tStep, problemId: `weather:${tier}:${si}` });
    ctx.sound('ding'); ctx.say(st.why);
    if (tier === 3 && si === 0) ctx.charm('charm.front');
    si++;
    if (si >= steps.length) {
      done = true;
      if (tier === 1) ctx.charm('charm.rain');
      task.textContent = 'Round complete. Keep playing with the sun and wind if you like.';
      checkBtn.style.display = 'none'; clear(choices);
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), 1500);
      return;
    }
    setTimeout(showStep, 1700);
  }
  function fail(hint: string) {
    if (!missed) { missed = true; attempts++; ctx.report({ skill: 'earth_science', level, correct: false, hintUsed: false, ms: performance.now() - tStep, problemId: `weather:${tier}:${si}` }); }
    ctx.sound('buzz'); ctx.say(hint);
  }
  function act() { if (done) return; const st = steps[si]; if (st.kind !== 'check') return; if (st.check!(sim, sun)) pass(st); else fail(st.hint!(sim, sun)); }
  function answer(i: number) { if (done) return; const st = steps[si]; if (i === st.answer) pass(st); else fail(st.needFront ? 'Press Cold front again and watch where the rain lands.' : 'Look at the picture again and think about what the lesson said.'); }

  function update(dt: number) { for (let k = 0; k < 3; k++) step(sim, sun, wind, dt, ctx.rng); }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    if (W < 16) return;
    const sx = W / sim.W, sy = H / sim.H;
    c.save(); c.scale(sx, sy);
    const w = sim.W, hh = sim.H;
    // sky darkens with clouds, sun glow
    c.fillStyle = '#bfe3f5'; c.fillRect(0, 0, w, hh);
    c.fillStyle = '#f5c542'; c.globalAlpha = 0.4 + sun * 0.6; c.beginPath(); c.arc(w * 0.12, 34, 14 + sun * 10, 0, 7); c.fill(); c.globalAlpha = 1;
    // land and mountain
    c.fillStyle = '#9ad67e'; c.beginPath(); c.moveTo(sim.seaEdge * w, hh); for (let x = sim.seaEdge * w; x <= w; x += 4) c.lineTo(x, groundAt(sim, x)); c.lineTo(w, hh); c.closePath(); c.fill();
    c.fillStyle = '#b9a99a'; c.beginPath(); c.moveTo(sim.mountain[0] * w, groundAt(sim, sim.mountain[0] * w)); for (let x = sim.mountain[0] * w; x <= sim.mountain[1] * w; x += 3) c.lineTo(x, groundAt(sim, x)); c.lineTo(sim.mountain[1] * w, hh); c.lineTo(sim.mountain[0] * w, hh); c.closePath(); c.fill();
    // sea: level rises a little with content
    const seaTop = hh * 0.8 - (sim.sea - 300) * 0.02;
    c.fillStyle = '#4f8fe6'; c.fillRect(0, Math.max(hh * 0.7, seaTop), sim.seaEdge * w, hh);
    // river: a band whose width shows the water in it
    const rw = Math.min(14, 2 + sim.river * 0.15);
    c.strokeStyle = '#7fd0f0'; c.lineWidth = rw; c.beginPath(); c.moveTo(sim.mountain[0] * w, groundAt(sim, sim.mountain[0] * w) + 2); c.quadraticCurveTo(w * 0.55, hh * 0.78, sim.seaEdge * w, hh * 0.8); c.stroke();
    // condensation line
    c.setLineDash([4, 6]); c.strokeStyle = 'rgba(42,42,46,.25)'; c.lineWidth = 1; c.beginPath(); c.moveTo(0, hh * sim.condY); c.lineTo(w, hh * sim.condY); c.stroke(); c.setLineDash([]);
    c.fillStyle = 'rgba(42,42,46,.45)'; c.font = '700 9px Nunito, sans-serif'; c.textAlign = 'left'; c.fillText('cold enough to condense', 4, hh * sim.condY - 3);
    // vapor
    c.fillStyle = 'rgba(255,255,255,.7)'; for (const v of sim.vapor) c.fillRect(v.x - 1, v.y - 1, 2, 2);
    // clouds
    for (const cl of sim.clouds) { const r = 8 + Math.min(18, cl.drops * 1.2); const dark = Math.min(1, cl.drops / 14); c.fillStyle = `rgb(${Math.round(250 - dark * 110)},${Math.round(250 - dark * 110)},${Math.round(252 - dark * 100)})`; c.beginPath(); c.arc(cl.x, cl.y, r, 0, 7); c.arc(cl.x - r * 0.8, cl.y + 4, r * 0.7, 0, 7); c.arc(cl.x + r * 0.8, cl.y + 4, r * 0.7, 0, 7); c.fill(); }
    // rain
    c.strokeStyle = '#4f8fe6'; c.lineWidth = 1.5; for (const d of sim.rain) { c.beginPath(); c.moveTo(d.x, d.y); c.lineTo(d.x - 1, d.y + 6); c.stroke(); }
    // front
    if (sim.front.active) { const fx = sim.front.x * w; c.fillStyle = 'rgba(79,143,230,.18)'; c.fillRect(0, 0, fx, hh * 0.8); c.fillStyle = '#2d5fb8'; for (let y = 10; y < hh * 0.75; y += 22) { c.beginPath(); c.moveTo(fx, y); c.lineTo(fx + 10, y + 7); c.lineTo(fx, y + 14); c.fill(); } }
    // wind arrows
    c.strokeStyle = 'rgba(42,42,46,.35)'; c.lineWidth = 2; for (let i = 0; i < 3; i++) { const y = 20 + i * 12, len = wind * 30; c.beginPath(); c.moveTo(w * 0.5 - len / 2, y); c.lineTo(w * 0.5 + len / 2, y); c.stroke(); }
    // tilted Earth inset for the seasons question
    if (showEarth) {
      c.fillStyle = 'rgba(255,255,255,.9)'; c.fillRect(w - 130, 8, 122, 90);
      c.fillStyle = '#f5c542'; c.beginPath(); c.arc(w - 110, 52, 14, 0, 7); c.fill();
      c.save(); c.translate(w - 45, 52); c.rotate(-0.41);
      c.fillStyle = '#4f8fe6'; c.beginPath(); c.arc(0, 0, 20, 0, 7); c.fill();
      c.fillStyle = '#5fbf6a'; c.beginPath(); c.arc(-4, -6, 7, 0, 7); c.fill();
      c.strokeStyle = '#2a2a2e'; c.lineWidth = 2; c.beginPath(); c.moveTo(0, -28); c.lineTo(0, 28); c.stroke();
      c.restore();
      c.fillStyle = '#2a2a2e'; c.font = '700 9px Nunito, sans-serif'; c.textAlign = 'left'; c.fillText('tilted 23 degrees', w - 126, 92);
    }
    c.restore();
    (readout.children[0] as HTMLElement).textContent = `Sea ${sim.sea}`;
    (readout.children[1] as HTMLElement).textContent = `Air ${sim.vapor.length}`;
    (readout.children[2] as HTMLElement).textContent = `Clouds ${sim.clouds.reduce((a, cl) => a + cl.drops, 0)}`;
    (readout.children[3] as HTMLElement).textContent = `Evap ${sim.evapRate.toFixed(0)}/s`;
  }

  const loop = makeLoop(update, draw);
  loop.start();
  showStep();
  return { destroy() { loop.stop(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'science.weather-water',
  title: 'Weather and Water',
  blurb: 'Sun lifts the sea, clouds drift, rain runs home. Make the water go round.',
  category: 'science',
  skills: ['earth_science'],
  levelRange: [1, 10],
  unlock: { stars: 11 },
  mount,
};
export default mod;
